import { createDependencyContext } from "app-base/web-shell/context"
import { useContext } from "react"

import { IAppResponse, IAuditLogEdit, ICruise, IDay, IDayResponse, IDayUpdateRequest, ILog, ILogResponse, IShip, IShipData, IShipUser, IStatus, ITrack, ITrip } from "../Types"
import { ApiUser } from "../Types/Generated/LogbookApi/ApiRuntime"
import type { ILogUpdateRequest } from "../Types/Generated/LogbookApp/LogApi"

// The write surface a DayView acquisition variant provides to its edit sub-views.
// Both variants expose the same shape: the New (flag-on) variant backs these with
// optimistic `DataStore.mutate` writes reconciled by the server broadcast; the
// Legacy (flag-off) variant backs them with plain typed-REST calls. Edit views call
// `GetDay().updateLog(...)` rather than the typed client directly, so the
// data-acquisition layer owns the write path (#1165). See Views/Day/writers.ts.
export interface DayWriters {
  updateLog: (logId: number, values: ILogUpdateRequest) => Promise<ILogResponse>;
  updateLogEngine: (logId: number, engine: string, on: boolean) => Promise<IAppResponse>;
  updateLogSails: (logId: number, sails: string[]) => Promise<IAppResponse>;
  updateDay: (values: IDayUpdateRequest) => Promise<IDayResponse>;
  createDay: (values: IDayUpdateRequest) => Promise<IDayResponse>;
  calculateDay: () => Promise<IAppResponse>;
}

export interface CruiseContextProps {
  cruise: ICruise;
  showLabels: boolean;
  trips: ITrip[];
}

const cruiseContext = createDependencyContext<CruiseContextProps>("CruiseContext");
export const CruiseContext = cruiseContext.Context;
export const GetCruise = () => useContext(CruiseContext)!;

export interface DayContextProps extends DayWriters {
  currentLog?: ILog;
  day: IDay;
  focus: boolean;
  // Whether the day's own trip is still current (`trip.completedAt == null`) — drives
  // the `editCurrent`/`editAll` shipUserLevel split for day/log mutations (permission
  // matrix, #931, tracker #827). Deliberately distinct from `isCurrentDay` (Focus's "is this the two
  // days Focus renders as today" UI concept) and from `focus` (Focus-tab-rendered flag):
  // neither tracks current/historical for days rendered outside Focus (e.g. Trip/DaysView's
  // history browsing).
  isCurrentTrip: boolean;
  logs: ILog[];
  statuses: IStatus[];
  tracks: ITrack[];
  // Audit trail for every Log (and, once wired, Day) field edit in this day — issue
  // #529's client-side editability parity (see Views/Log/editability.ts) needs this to
  // know whether an NMEA-supplied field has already been manually superseded, and the
  // planned edit-history UI needs it to list who changed what and when.
  auditLogEdits: IAuditLogEdit[];
  // Merges a `logs.update` response's `auditLogEdits` (existing + newly created for
  // that one log) into the day's list — `AuditLogEdit` is `@typegen-skip`'d so it
  // isn't pushed over a live subscription like `logs`/`statuses` are.
  recordAuditLogEdits: (edits: IAuditLogEdit[]) => void;
  // Keeps the authoritative Day returned by this view's own save available to sibling
  // log rows until the owning Trip/Focus snapshot catches up.
  recordSavedDay?: (day: IDay) => void;
}

const dayContext = createDependencyContext<DayContextProps>("DayContext");
export const DayContext = dayContext.Context;
export const GetDay = () => useContext(DayContext)!;

const logContext = createDependencyContext<ILog>("LogContext");
export const LogContext = logContext.Context;
export const GetLog = () => useContext(LogContext)!;

export interface ShipContextProps {
  ship?: IShip;
  shipUser?: IShipUser;

  requireUserLevel: (level: string) => boolean;
  // Backs the generated `<Api>.<method>.available(user)` predicates — see
  // Views/App/Context.tsx's AppViewData.apiUser for how ship/account levels resolve.
  // Optional like `ship`/`shipUser`: harnesses that don't exercise `.available()`
  // gating can omit it, mirroring the generated predicate's own `user?: ApiUser`.
  apiUser?: ApiUser;
}

const shipContext = createDependencyContext<ShipContextProps>("ShipContext");
export const ShipContext = shipContext.Context;
export const GetShipContext = () => useContext(ShipContext)!;
export const GetShip = () => GetShipContext().ship!;
export const FindShip = () => GetShipContext().ship!;

export interface TripContextProps {
  trip: ITrip;
  days: IDay[];
}

const tripContext = createDependencyContext<TripContextProps>("TripContext");
export const TripContext = tripContext.Context;
export const GetTrip = () => useContext(TripContext)!;

// Shared ship-print context. Owned here (not in a view file) because the Print family
// provides it (PrintView) and its settings panel consumes it, while the PrintShip family
// renders against it — see Views/PrintShipView.tsx and Views/Print/SettingsView.tsx.
export enum ShipDataType {
  short = 1,
  details = 2,
}

export interface PrintShipViewContextData {
  includeShipData: boolean;
  setIncludeShipData: (value: boolean) => void;
  includeShipDataType: ShipDataType;
  setIncludeShipDataType: (value: ShipDataType) => void;
  includeUserList: boolean;
  setIncludeUserList: (value: boolean) => void;
  data?: IShipData | IShip;
}

const printShipViewContext = createDependencyContext<PrintShipViewContextData>("PrintShipViewContext");
export const PrintShipViewContext = printShipViewContext.Context;
export const GetPrintShipViewContext = () => useContext(PrintShipViewContext)!;
